export type ApplicationReminderTokenCategory = "guardian" | "patient" | "application" | "clinic";

export type ApplicationReminderTokenKey =
  | "RESPONSAVEL_NOME"
  | "ANIMAL_NOME"
  | "APLICACAO_NOME"
  | "APLICACAO_DATA_PROXIMA"
  | "CLINICA_NOME";

export type ApplicationReminderTokenDefinition = {
  key: ApplicationReminderTokenKey;
  category: ApplicationReminderTokenCategory;
  mapping: string;
  example: string;
};

export const APPLICATION_REMINDER_TOKEN_DEFINITIONS: ApplicationReminderTokenDefinition[] = [
  { key: "RESPONSAVEL_NOME", category: "guardian", mapping: "owner.name", example: "Joao da Silva" },
  { key: "ANIMAL_NOME", category: "patient", mapping: "pet.name", example: "Thor" },
  { key: "APLICACAO_NOME", category: "application", mapping: "applications.name", example: "V10" },
  {
    key: "APLICACAO_DATA_PROXIMA",
    category: "application",
    mapping: "applications.next_dose_date",
    example: "25/03/2026",
  },
  { key: "CLINICA_NOME", category: "clinic", mapping: "clinic.name", example: "Akuna Vet" },
];

export const APPLICATION_REMINDER_TEMPLATE_DEFAULT_BODY =
  "Oi {RESPONSAVEL_NOME}, passando para lembrar que {ANIMAL_NOME} tem {APLICACAO_NOME} prevista para {APLICACAO_DATA_PROXIMA}.";

export const APPLICATION_REMINDER_TEMPLATE_SAMPLE_VALUES = APPLICATION_REMINDER_TOKEN_DEFINITIONS.reduce(
  (acc, item) => {
    acc[item.key] = item.example;
    return acc;
  },
  {} as Record<ApplicationReminderTokenKey, string>,
);

const TOKEN_PATTERN = /\{([A-Z0-9_]+)\}/g;

const definitionByKey = new Map(APPLICATION_REMINDER_TOKEN_DEFINITIONS.map((item) => [item.key, item]));

const isApplicationReminderTokenKey = (value: string): value is ApplicationReminderTokenKey =>
  definitionByKey.has(value as ApplicationReminderTokenKey);

export const extractApplicationReminderTokens = (input: string): string[] => {
  const tokens: string[] = [];
  for (const match of input.matchAll(TOKEN_PATTERN)) {
    const key = match[1];
    if (!tokens.includes(key)) tokens.push(key);
  }
  return tokens;
};

export const findUnknownApplicationReminderTokens = (input: string): string[] =>
  extractApplicationReminderTokens(input).filter((token) => !isApplicationReminderTokenKey(token));

export const buildApplicationReminderTemplateMapping = (
  tokens: Array<ApplicationReminderTokenKey | string>,
): Record<string, string> => {
  const mapping: Record<string, string> = {};
  tokens.forEach((token, index) => {
    if (!isApplicationReminderTokenKey(token)) return;
    mapping[String(index + 1)] = definitionByKey.get(token)!.mapping;
  });
  return mapping;
};

export const buildApplicationReminderMetaTemplate = (input: string) => {
  const unknownTokens = findUnknownApplicationReminderTokens(input);
  if (unknownTokens.length > 0) {
    throw new Error(`unknown_tokens:${unknownTokens.join(",")}`);
  }

  const tokens = extractApplicationReminderTokens(input).filter(isApplicationReminderTokenKey);
  const tokenPosition = new Map(tokens.map((token, index) => [token, index + 1]));
  const text = input.replace(TOKEN_PATTERN, (match, token: string) => {
    const position = tokenPosition.get(token as ApplicationReminderTokenKey);
    return position ? `{{${position}}}` : match;
  });

  return {
    text,
    tokens,
    examples: tokens.map((token) => definitionByKey.get(token)!.example),
    mapping: buildApplicationReminderTemplateMapping(tokens),
  };
};

export const renderApplicationReminderPreview = (
  input: string,
  values: Partial<Record<ApplicationReminderTokenKey, string>> = APPLICATION_REMINDER_TEMPLATE_SAMPLE_VALUES,
) =>
  input.replace(TOKEN_PATTERN, (match, token: string) => {
    if (!isApplicationReminderTokenKey(token)) return match;
    return values[token] ?? "";
  });
